/* global globalThis */
import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { useSsoCart } from "../../.././../contexts/SsoCartContext";
import CartItemCard from "./CartItemCard";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import CircularProgress from "@mui/material/CircularProgress";
import SsoServices from "../../../../services/SsoServices";
import {
  ADD_TO_CART,
  CHECKOUT_CART,
} from "../../../../constants/api-constants";
import { entityTypeEnum } from "../../../../constants/common";
import "./cart.css";

const ONE_TIME_FREQUENCY = 99;

const FREQUENCY_SORT_ORDER = {
  1: 1,
  2: 2,
  3: 3,
  4: 4,
  99: 5,
};

const getItemSubscriptions = (item) => {
  if (item?.entityType === entityTypeEnum.ADDIN) {
    return item.addIn?.subscriptions || [];
  }
  return item.content?.subscriptions || [];
};

const getSubscriptionId = (subscription) => {
  return (
    subscription?.contentSubscriptionId ?? subscription?.addInSubscriptionId
  );
};

const findSubscriptionById = (subscriptions, selectedSubId) => {
  return subscriptions.find(
    (sub) => String(getSubscriptionId(sub)) === String(selectedSubId),
  );
};

const getGroupedEntityTypes = (items) => {
  return [...new Set(items.map((item) => item?.entityType).filter(Boolean))];
};
/*istanbul ignore next */
const CartPage = () => {
  const history = useHistory();
  const { cart, cartMetadata, getCartDetails, count } = useSsoCart();
  const [loading, setLoading] = useState(true);
  const [checkoutLoadingGroup, setCheckoutLoadingGroup] = useState(null);
  const [selectedSubscriptions, setSelectedSubscriptions] = useState({});

  useEffect(() => {
    const fetchCart = async () => {
      setLoading(true);
      await getCartDetails();
      setLoading(false);
    };
    fetchCart();
  }, []);

  useEffect(() => {
    const initialSelections = {};
    cart.forEach((item) => {
      const subscriptions = getItemSubscriptions(item);
      const currentSub = subscriptions.find(
        (sub) =>
          sub.frequency === item.frequency &&
          Boolean(sub.isRecurring) === Boolean(item.isRecurring) &&
          Number(sub.actualValue || 0) === Number(item.amount || 0),
      );
      initialSelections[item.cartItemId] = currentSub
        ? getSubscriptionId(currentSub)
        : getSubscriptionId(subscriptions[0]) || "free";
    });
    setSelectedSubscriptions(initialSelections);
  }, [cart]);

  // Reset checkout loading state when user returns to the page
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        setCheckoutLoadingGroup(null);
      }
    };
    setCheckoutLoadingGroup(null);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  const handleSubscriptionChange = async (item, newSubscriptionId) => {
    try {
      const subscriptions = getItemSubscriptions(item);
      const newSubscription = findSubscriptionById(
        subscriptions,
        newSubscriptionId,
      );

      if (!newSubscription) {
        console.error("Selected subscription not found");
        return;
      }

      // Prepare payload for ADD_TO_CART API
      const payload = {
        itemId: item.itemId || item.content?.contentId,
        amount: newSubscription.actualValue,
        transactionType: item.transactionType || 2,
        entityType: item?.entityType,
        frequency: newSubscription.frequency,
        isRecurring: newSubscription.isRecurring,
      };

      // Call ADD_TO_CART API to update the cart item
      const response = await SsoServices.ssoPost(ADD_TO_CART, payload);

      if (response.responseStatus?.isSuccess) {
        // Update local state
        setSelectedSubscriptions((prev) => ({
          ...prev,
          [item.cartItemId]: newSubscriptionId,
        }));

        // Refresh cart to get updated data
        await getCartDetails();
      } else {
        console.error(
          "Failed to update subscription:",
          response.responseStatus?.error,
        );
      }
    } catch (error) {
      console.error("Error updating subscription:", error);
    }
  };

  const handleCheckout = async (
    frequency,
    isRecurring,
    trialDurationDays = 0,
    entityTypes = [],
    loadingGroupKey = null,
  ) => {
    const groupKey =
      loadingGroupKey || `${frequency}_${isRecurring}_${trialDurationDays}`;
    try {
      setCheckoutLoadingGroup(groupKey);

      // Get the base URL for return
      const baseUrl = globalThis.location.origin;

      const payload = {
        expectedCartRowVersion: cartMetadata.rowVersion,
        isRecurring: isRecurring,
        frequency: frequency,
        entityType: entityTypes,
        successUrl: `${baseUrl}/payment-process`,
        cancelUrl: `${baseUrl}/purchase/cart`,
        ...(frequency !== 99 && trialDurationDays > 0 ? { trialPeriodDays: trialDurationDays } : {}),
      };

      const response = await SsoServices.ssoPost(CHECKOUT_CART, payload);
      if (response.responseStatus?.isSuccess) {
        const result = response.result;
        if (result?.isSuccessful && result?.stripeCheckoutUrl === "") {
          history.push("/payment-success");
          // Keep loading state - navigating away
        } else if (result?.stripeCheckoutUrl) {
          globalThis.location.href = result.stripeCheckoutUrl;
          // Keep loading state - navigating away
        } else {
          console.error("Unexpected response format:", result);
          setCheckoutLoadingGroup(null); // Only reset on error
        }
      } else {
        console.error("Checkout failed:", response.responseStatus?.error);
        setCheckoutLoadingGroup(null); // Only reset on error
      }
    } catch (error) {
      console.error("Error during checkout:", error);
      setCheckoutLoadingGroup(null); // Only reset on error
    }
  };

  const determineFrequencyGroup = (item, selectedSubId) => {
    const subscriptions = getItemSubscriptions(item);
    
    const isFreeOrZeroCost = (subscription, itemData) => {
      const costType = Number(subscription?.costType ?? 0);
      const actualValue = Number(subscription?.actualValue ?? itemData?.amount ?? 0);
      
      // costType === 1 means always free
      if (costType === 1) return true;
      
      // costType === 2 with zero amount means free
      if (costType === 2 && actualValue === 0) return true;
      
      // Recurring subscription with zero value should be treated as one-time free
      if (actualValue === 0 && subscription?.frequency < ONE_TIME_FREQUENCY) return true;
      
      return false;
    };
    
    const toGroup = (subscription) => {
      const trialDurationDays = Number(subscription?.trialDurationDays || 0);
      
      // If it's free or zero cost, group as one-time free
      if (isFreeOrZeroCost(subscription, item)) {
        return {
          frequency: ONE_TIME_FREQUENCY,
          isRecurring: false,
          trialDurationDays: 0,
          isFree: true,
        };
      }
      
      let subscriptionFrequency = Number(
        subscription?.frequency ?? ONE_TIME_FREQUENCY,
      );

      // If actual value is 0 for recurring, treat as one-time
      if (subscriptionFrequency < ONE_TIME_FREQUENCY && 
          Number(subscription?.actualValue ?? 0) === 0) {
        subscriptionFrequency = ONE_TIME_FREQUENCY;
      }

      if (subscriptionFrequency === ONE_TIME_FREQUENCY) {
        return {
          frequency: ONE_TIME_FREQUENCY,
          isRecurring: false,
          trialDurationDays,
          isFree: false,
        };
      }

      return {
        frequency: subscriptionFrequency,
        isRecurring: Boolean(subscription?.isRecurring),
        trialDurationDays,
        isFree: false,
      };
    };

    // Free content - no subscriptions
    if (subscriptions.length === 0) {
      return {
        frequency: ONE_TIME_FREQUENCY,
        isRecurring: false,
        trialDurationDays: 0,
        isFree: true,
      };
    }

    const selectedSub = findSubscriptionById(subscriptions, selectedSubId);

    // No subscription selected - fallback to first subscription
    if (!selectedSub) {
      return toGroup(subscriptions[0]);
    }

    return toGroup(selectedSub);
  };

  const groupByFrequency = () => {
    const groups = {};
    cart.forEach((item) => {
      const selectedSubId = selectedSubscriptions[item.cartItemId];
      const { frequency, isRecurring, trialDurationDays, isFree } = determineFrequencyGroup(
        item,
        selectedSubId,
      );
      const groupKey = `${frequency}_${isRecurring}_${trialDurationDays}_${isFree ? 'free' : 'paid'}`;

      if (!groups[groupKey]) {
        groups[groupKey] = {
          frequency,
          isRecurring,
          trialDurationDays,
          isFree,
          items: [],
        };
      }
      groups[groupKey].items.push(item);
    });
    return groups;
  };

  const getFrequencyLabel = (frequency, isRecurring) => {
    const labels = {
      1: "Monthly",
      2: "Quarterly",
      3: "Semi-Annual",
      4: "Annual",
      99: "One Time",
    };
    return labels[frequency] || "Unknown";
  };

  const getFrequencyDescription = (frequency, isRecurring, trialDurationDays = 0, isFree = false) => {
    if (isFree) {
      return "Free item";
    }

    const labels = {
      1: "Billed monthly until cancelled",
      2: "Billed quarterly until cancelled",
      3: "Billed semi-annually until cancelled",
      4: "Billed annually until cancelled",
      99: "One-time purchase",
    };

    const baseDescription = labels[frequency] || "";

    if (Number(trialDurationDays) > 0 && frequency !== ONE_TIME_FREQUENCY) {
      return `${trialDurationDays} Days Trial, then ${baseDescription.toLowerCase()}`;
    }

    return baseDescription;
  };

  const calculateGroupTotal = (items) => {
    return items.reduce((sum, item) => {
      const subscriptions = getItemSubscriptions(item);
      const selectedSubId = selectedSubscriptions[item.cartItemId];
      const selected = findSubscriptionById(subscriptions, selectedSubId);
      return sum + (selected ? selected.actualValue : item.amount || 0);
    }, 0);
  };

  const groupedCart = groupByFrequency();
  const sortedGroupedCart = Object.entries(groupedCart).sort(
    ([, leftGroup], [, rightGroup]) => {
      const leftFrequencyOrder =
        FREQUENCY_SORT_ORDER[leftGroup.frequency] || Number.MAX_SAFE_INTEGER;
      const rightFrequencyOrder =
        FREQUENCY_SORT_ORDER[rightGroup.frequency] || Number.MAX_SAFE_INTEGER;

      if (leftFrequencyOrder !== rightFrequencyOrder) {
        return leftFrequencyOrder - rightFrequencyOrder;
      }

      if (leftGroup.isRecurring !== rightGroup.isRecurring) {
        return leftGroup.isRecurring ? -1 : 1;
      }

      return 0;
    },
  );
  const hasMultipleFrequencies = sortedGroupedCart.length > 1;

  const renderCartContent = () => {
    if (loading) {
      return (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "400px",
            flexDirection: "column",
            gap: "16px",
          }}
        >
          <CircularProgress size={50} style={{ color: "white" }} />
          <div style={{ color: "white", fontSize: "16px" }}>
            Loading Cart Details..
          </div>
        </div>
      );
    }

    if (cart.length === 0) {
      return (
        <div style={{ color: "white", textAlign: "center", marginTop: 80 }}>
          🛒 <br />
          No items in cart.
        </div>
      );
    }

    return (
      <>
        {hasMultipleFrequencies && (
          <div className="cart-info-message">
            <InfoOutlinedIcon style={{ fontSize: "20px", color: "#ff3b3b" }} />
            <div>
              <strong>
                Your cart contains items with different billing frequencies.
              </strong>
              <br />
              These items need to be purchased separately.
            </div>
          </div>
        )}

        {sortedGroupedCart.map(([groupKey, group]) => {
          const freq = group.frequency;
          const isRecurring = group.isRecurring;
          const trialDurationDays = group.trialDurationDays || 0;
          const isFree = group.isFree || false;
          const items = group.items;
          const entityTypes = getGroupedEntityTypes(items);
          const groupTotal = calculateGroupTotal(items);

          return (
            <div key={groupKey} className="frequency-group">
              <div className="frequency-header">
                <h3>{getFrequencyLabel(freq, isRecurring)} Subscriptions</h3>
                <p>{getFrequencyDescription(freq, isRecurring, trialDurationDays, isFree)}</p>
              </div>

              <div className="frequency-items">
                {items.map((item) => (
                  <CartItemCard
                    key={item.cartItemId}
                    item={item}
                    selectedSubscription={
                      selectedSubscriptions[item.cartItemId]
                    }
                    onSubscriptionChange={handleSubscriptionChange}
                    userId={cartMetadata?.userId}
                  />
                ))}
              </div>

              <div className="frequency-summary">
                <div className="summary-row">
                  <span>Total Items</span>
                  <span>{items.length}</span>
                </div>
                <div className="summary-row total">
                  <span>Subtotal</span>
                  <span>${groupTotal.toFixed(2)}</span>
                </div>
              </div>

              <div className="tax-notice">
                Taxes and additional charges will be calculated in the next step
                based on billing and shipping details.
              </div>

              <button
                className="checkout-btn-group"
                onClick={() =>
                  handleCheckout(
                    freq,
                    isRecurring,
                    trialDurationDays,
                    entityTypes,
                    groupKey,
                  )
                }
                disabled={checkoutLoadingGroup !== null}
              >
                {checkoutLoadingGroup === groupKey ? (
                  <>
                    <CircularProgress
                      size={20}
                      style={{ color: "white", marginRight: "8px" }}
                    />
                    PROCESSING...
                  </>
                ) : (
                  <>
                    PROCEED TO CHECKOUT -{" "}
                    {getFrequencyLabel(freq, isRecurring).toUpperCase()}
                  </>
                )}
              </button>
            </div>
          );
        })}
      </>
    );
  };

  return (
    <div className="cart-wrapper">
      <div className="cart-header">
        <button className="cart-header-icon" onClick={() => history.push("/purchase")}>
          ← <span>Cart</span>
        </button>
        <div className="cart-header-icon disabled">
          <ShoppingCartOutlinedIcon />{" "}
          {count > 0 && <span className="cart-badge">{count}</span>}
        </div>
      </div>

      {renderCartContent()}
    </div>
  );
};

export default CartPage;
